import { z } from "zod";
import { SystemEvent } from "@erp/shared-types";
import { defineStepConfig } from "../../registry";

/**
 * Emit System Event — publishes a draft-sales-order routing/handoff event so a
 * downstream workflow is triggered. The reusable handoff primitive for OMS v3:
 *
 *   - DRAFT_SALES_ORDER_ROUTED (routing org → target org): set
 *     `targetOrganizationId` (e.g. from classify_organization's
 *     matched_organization_id) so the engine starts the target org's
 *     event-triggered workflow in THAT org (cross-org routing).
 *   - DRAFT_SALES_ORDER_READY (intra-org wf2 → wf3 handoff): omit the target;
 *     the event routes within the emitting org.
 *
 * Backend-only (publishes via SystemEventPublisher); handler in
 * backend/src/workflows/steps/control-flow/.
 */
export const emitSystemEventStep = defineStepConfig({
  type: "emit_system_event",
  name: "Emit System Event",
  description:
    "Publishes a draft sales-order routing/handoff event to trigger a downstream (optionally cross-organization) workflow",
  category: "Control Flow",
  // NOTE: the runtime schema is permissive (per-event requirements are
  // enforced in the handler) — the CANVAS socket requiredness comes from
  // `generateEmitSystemEventInputSockets` (backend dynamicInputSockets.ts),
  // which keeps draftSalesOrderId required for the draft events (the OMS
  // router relies on that for dead-pruning) and swaps to a required
  // inboundEmailId for DISCOUNT_REQUEST_DOCUMENT_ROUTED.
  inputSchema: z.object({
    draftSalesOrderId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "The draft sales order the event is about (required for the draft events)",
      ),
    targetOrganizationId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "For cross-org events (DRAFT_SALES_ORDER_ROUTED, DISCOUNT_REQUEST_DOCUMENT_ROUTED): the organization to route to. Omit for intra-org handoffs.",
      ),
    inboundEmailId: z
      .string()
      .uuid()
      .optional()
      .describe(
        "Originating inbound email id (required for DISCOUNT_REQUEST_DOCUMENT_ROUTED)",
      ),
  }),
  outputSchema: z.object({
    emitted: z.boolean().describe("Whether the event was published"),
    eventType: z.string().describe("The event type that was emitted"),
  }),
  configSchema: z.object({
    eventType: z
      .union([
        z.literal(SystemEvent.DRAFT_SALES_ORDER_ROUTED),
        z.literal(SystemEvent.DRAFT_SALES_ORDER_READY),
        z.literal(SystemEvent.DRAFT_SALES_ORDER_UNCLASSIFIED),
        z.literal(SystemEvent.DISCOUNT_REQUEST_DOCUMENT_ROUTED),
      ])
      .describe("Which routing/handoff event to emit"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "1 minute",
    retry: {
      maximumAttempts: 3,
    },
  },
});
